import React from 'react';
import { connect } from 'react-redux';
import { PropTypes } from 'prop-types';
import withStyles from '@mui/styles/withStyles';
import { styled } from '@mui/material/styles';
import {
  Box,
  Grid,
  Button,
  Typography,
  Icon,
  Avatar,
  Stack,
} from '@mui/material';
import Tooltip, { tooltipClasses } from '@mui/material/Tooltip';
import { Link } from 'react-router-dom';
import Chart from 'react-apexcharts';
import { withIsMobile, withRouter, StringUtil, ColorUtil } from 'utils';
import { LoadingView } from 'components';
import { StoreUtil, LocationTypeUtil } from 'doctivity-shared/utils';
import { ProviderCommentsDialog } from 'components';
import { upsertProviderComment } from 'store/actions/providerCommentsActions';
import { showConfirmationDialog } from 'store/actions/systemActions';
import {
  loadFavoriteProviders,
  loadProviderTopDx,
  loadProviderTopPx,
  loadProviderTopServiceLines,
} from 'store/actions/dashboardActions';

const styles = (theme) => ({
  listItemRow: {
    border: '1px solid #E0E0E0',
    '&:hover': {
      background: '#F5F5F5',
    },
  },
  affiliatedIcon: {
    fontSize: '16px',
    verticalAlign: 'text-top',
    color: '#2082F4',
    marginTop: '2px',
    marginRight: '2px',
  },
  favoriteIcon: {
    fontSize: '21px',
    verticalAlign: 'text-top',
    color: '#E300B8',
    marginRight: '10px',
  },
  providerName: {
    color: '#333333',
    fontSize: '16px',
    [theme.breakpoints.down('md')]: {
      fontSize: '14px',
    },
  },
  credential: {
    [theme.breakpoints.between(1440, 1800)]: {
      display: 'none',
    },

    [theme.breakpoints.down('xl')]: {
      display: 'inline',
    },
    [theme.breakpoints.down('lg')]: {
      display: 'none',
    },
  },
  specialtyContainer: {},
  providerSpecialty: {
    fontSize: '14px',
    color: '#333',
    fontWeight: 300,
    [theme.breakpoints.down('md')]: {
      fontSize: '12px',
    },
  },
  locationContainer: {
    [theme.breakpoints.down('lg')]: {
      display: 'none',
    },
  },
  listItemDetailContainer: {
    padding: '20px 25px',
    [theme.breakpoints.down('md')]: {
      overflow: 'scroll',
    },
  },

  listItemDetailGrid: {
    [theme.breakpoints.down('md')]: {
      minWidth: '800px',
    },
  },

  analyticsContainer: {
    background: '#FFF',
    boxShadow: '0px 0px 10px #0000001F',
    borderRadius: '10px',
    padding: '10px 15px',
    minHeight: '360px',
  },
});

const ListItemActionButton = styled(Button)({
  fontSize: '12px',
  fontWeight: '500',
  border: '1px solid #214E56',
  borderRadius: '20px',
  color: '#214E56',
  padding: '0 5px',
  textTransform: 'none',
});

const ListItemLink = styled(Button)({
  fontSize: '12px',
  color: '#214E56',
  textTransform: 'none',
  fontWeight: '400',
  textDecoration: 'underline',
  padding: '3px 5px',
  minWidth: 0,
  '&:hover': {
    background: 'none',
    textDecoration: 'underline',
  },
});

const CodeTooltip = styled(({ className, ...props }) => (
  <Tooltip {...props} classes={{ popper: className }} />
))(({}) => ({
  [`& .${tooltipClasses.tooltip}`]: {
    backgroundColor: '#FFFFFF',
    maxWidth: '192px',
    fontSize: '14px',
    textAlign: 'center',
    borderRadius: '10px',
    boxShadow: '0px 3px 6px #0000003D',
    color: '#333333',
    padding: '10px',
  },
}));

class DashboardProvidersPage extends React.Component {
  constructor(props) {
    super(props);

    this.state = {
      selectedProvider: 0,
      selectedTopCodesType: 'dx',
      newNoteDialogOpen: false,
      newNoteForProviderId: -1,
    };
  }

  componentDidMount() {
    if (StoreUtil.needsLoadNoCache(this.props.favoriteProviders)) {
      this.fetchData();
    }
  }

  componentDidUpdate(prevProps) {
    if (
      this.props.app.selectedClient !== prevProps.app.selectedClient ||
      StoreUtil.needsLoadNoCache(this.props.favoriteProviders)
    ) {
      this.fetchData();
    }

    if (
      !StoreUtil.isLoaded(prevProps.favoriteProviders) &&
      StoreUtil.isLoaded(this.props.favoriteProviders)
    ) {
      this.fetchSelected();
    }
  }

  render() {
    const { classes, dashboard, favoriteProviders, isMobile, isEarlyMobile } =
      this.props;

    if (
      !dashboard ||
      !favoriteProviders ||
      !StoreUtil.isLoaded(favoriteProviders)
    ) {
      return <LoadingView />;
    }

    return (
      <>
        <Grid
          container
          sx={{
            px: 3,
            py: 2,
          }}
        >
          <Grid item xs={isMobile || isEarlyMobile ? 12 : 6}>
            {this.props.favoriteProviders.data.map((favorite, index) => {
              return (
                <Stack key={favorite.provider_id}>
                  <Box
                    sx={{
                      border: '1px solid #ebebeb',
                      height: 40,
                      borderRadius: '5px',
                      borderTopRightRadius:
                        this.state.selectedProvider === index ? '0' : '5px',
                      borderBottomRightRadius:
                        this.state.selectedProvider === index ? '0' : '5px',
                      background:
                        this.state.selectedProvider === index
                          ? '#ebebeb'
                          : '#ffffff',
                      '&:hover': {
                        background: '#F5F5F5',
                      },
                    }}
                    onClick={() =>
                      this.selectProvider(favorite.provider_id, index)}
                  >
                    <Grid
                      container
                      columnSpacing={1}
                      alignItems='center'
                      sx={{
                        height: '100%',
                      }}
                    >
                      <Grid item xs={6} sm={5} md={5} lg={4} xl={4}>
                        <Box
                          sx={{
                            paddingLeft: 1,
                          }}
                        >
                          {favorite.is_affiliated ? (
                            <Typography component='span'>
                              <Icon className={classes.affiliatedIcon}>
                                hub
                              </Icon>
                            </Typography>
                          ) : (
                            <Box
                              component='span'
                              sx={{
                                width: '18px',
                                height: '14px',
                                display: 'inline-block',
                              }}
                            ></Box>
                          )}
                          <Typography component='span'>
                            <Icon className={classes.favoriteIcon}>
                              favorite
                            </Icon>
                          </Typography>
                          <Typography
                            component='span'
                            className={classes.providerName}
                          >
                            {`${StringUtil.toTitleCase(
                              favorite.first_name
                            )} ${StringUtil.toTitleCase(favorite.last_name)}`}
                            <Typography
                              component='span'
                              className={classes.credential}
                              fontWeight='300'
                            >
                              {`${
                                favorite.credential
                                  ? ', ' + favorite.credential
                                  : ''
                              }`}
                            </Typography>
                          </Typography>
                        </Box>
                      </Grid>
                      <Box
                        component={Grid}
                        item
                        xs={3}
                        sm={4}
                        md={3}
                        lg={3}
                        xl={3}
                        className={classes.specialtyContainer}
                      >
                        <Typography className={classes.providerSpecialty}>
                          {favorite.specialty}
                        </Typography>
                      </Box>
                      <Box
                        component={Grid}
                        item
                        md={2}
                        lg={2}
                        xl={2}
                        className={classes.locationContainer}
                      >
                        <Typography
                          fontSize='14px'
                          color='#333333'
                          fontWeight='300'
                        >
                          {favorite.location_city &&
                            favorite.location_state &&
                            `${StringUtil.toTitleCase(
                              favorite.location_city
                            )}, ${favorite.location_state}`}
                        </Typography>
                      </Box>
                      <Grid item xs={3} md={4} lg={3} xl={3}>
                        <Box
                          sx={{
                            paddingRight: 1,
                            textAlign: 'right',
                          }}
                        >
                          {!isMobile &&
                            this.state.selectedProvider === index && (
                              <ListItemLink
                                component={Link}
                                to={'/providers/' + favorite.provider_id}
                              >
                                View
                              </ListItemLink>
                            )}
                          <ListItemActionButton
                            onClick={() =>
                              this.openNewNoteDialog(favorite.provider_id)}
                          >
                            + New Note
                          </ListItemActionButton>
                        </Box>
                      </Grid>
                    </Grid>
                  </Box>
                  <Box>
                    {this.state.selectedProvider === index &&
                      (isMobile || isEarlyMobile) &&
                      this.renderSelectedProviderDetails()}
                  </Box>
                </Stack>
              );
            })}
          </Grid>
          {!isMobile && !isEarlyMobile && this.renderSelectedProviderDetails()}
        </Grid>

        {this.state.newNoteDialogOpen && (
          <ProviderCommentsDialog
            clientId={this.props.app.selectedClient}
            open={this.state.newNoteDialogOpen}
            onSave={this.onSaveNewNote}
            onClose={this.closeNewNoteDialog}
          />
        )}
      </>
    );
  }

  renderSelectedProviderDetails = () => {
    const { classes, dashboard, favoriteProviders, isMobile, isEarlyMobile } =
      this.props;

    let selectedFavoriteProvider =
      favoriteProviders.data[this.state.selectedProvider];
    let loadedTopServiceLinesForSelectedProvider = false;
    let loadedTopCodes = false;
    let categories = [];
    let series = [
      // the order of these can NOT change it matches the id numbers for location_type
      {
        name: 'Ambulatory Surgery Center',
        data: [],
      },
      {
        name: 'Outpatient',
        data: [],
      },
      {
        name: 'Office',
        data: [],
      },
      {
        name: 'Inpatient',
        data: [],
      },
      {
        name: 'Lab',
        data: [],
      },
    ];
    let topCodes = [];
    const topCodesColors = ['#9937BA', '#fa9090', '#F219D0'];

    if (selectedFavoriteProvider) {
      const topServiceLinesDataForSelected =
        dashboard[
          'analytics-provider-topservicelines-' +
            selectedFavoriteProvider.provider_id
        ];
      if (StoreUtil.isLoaded(topServiceLinesDataForSelected)) {
        loadedTopServiceLinesForSelectedProvider = true;

        const groupByServiceLine = Object.groupBy(
          topServiceLinesDataForSelected.data,
          ({ name }) => name
        );
        categories = Object.keys(groupByServiceLine).map((serviceLineName) => {
          return serviceLineName.split(' ');
        });

        for (const serviceLineName of Object.keys(groupByServiceLine)) {
          for (const [
            seriesLocationTypeIndex,
            seriesLocationType,
          ] of series.entries()) {
            let idx = groupByServiceLine[serviceLineName].findIndex(
              (serviceLineRow) =>
                seriesLocationType.name ===
                LocationTypeUtil.getName(serviceLineRow.location_type)
            );
            if (idx > -1) {
              series[seriesLocationTypeIndex].data.push({
                x: serviceLineName,
                y: groupByServiceLine[serviceLineName][idx].total_patients,
              });
            } else {
              series[seriesLocationTypeIndex].data.push({
                x: serviceLineName,
                y: 0,
                lessThan11: true,
              });
            }
          }
        }
      }

      const topCodesForSelected =
        dashboard[
          'analytics-provider-top' +
            this.state.selectedTopCodesType +
            '-' +
            selectedFavoriteProvider.provider_id
        ];
      if (StoreUtil.isLoaded(topCodesForSelected)) {
        loadedTopCodes = true;

        topCodes = topCodesForSelected.data.sort(
          (topCodeA, topCodeB) => topCodeA.claim_count - topCodeB.claim_count
        );
      }
    }

    return (
      <Grid
        item
        xs={isMobile || isEarlyMobile ? 12 : 6}
        sx={{ background: '#EBEBEB' }}
      >
        <Box className={classes.listItemDetailContainer}>
          <Grid container spacing={2} className={classes.listItemDetailGrid}>
            <Grid item xs={6}>
              <Box className={classes.analyticsContainer}>
                {!loadedTopCodes ? (
                  <LoadingView />
                ) : (
                  <>
                    <Typography
                      fontSize='18px'
                      color='#333333'
                      textAlign='center'
                    >
                      Top 3{' '}
                      <Typography
                        component='span'
                        sx={{
                          fontSize: '18px',
                          textDecoration:
                            this.state.selectedTopCodesType === 'dx'
                              ? 'underline'
                              : 'none',
                          color:
                            this.state.selectedTopCodesType === 'dx'
                              ? '#333333'
                              : '#7E7E7E',
                          cursor: 'pointer',
                          margin: '0 0 0 10px',
                        }}
                        onClick={this.clickTopDx}
                      >
                        DX Codes
                      </Typography>
                      <Typography
                        component='span'
                        sx={{
                          fontSize: '18px',
                          textDecoration:
                            this.state.selectedTopCodesType === 'px'
                              ? 'underline'
                              : 'none',
                          color:
                            this.state.selectedTopCodesType === 'px'
                              ? '#333333'
                              : '#7E7E7E',
                          cursor: 'pointer',
                          margin: '0 0 0 10px',
                        }}
                        onClick={this.clickTopPx}
                      >
                        PX Codes
                      </Typography>
                    </Typography>
                    <Box pt={2}>
                      <Grid
                        container
                        justifyContent='center'
                        alignItems='center'
                      >
                        {topCodes.map((topCode, index) => {
                          return (
                            <Box
                              key={topCode.code}
                              sx={{ margin: '0 10px 0 10px' }}
                            >
                              <CodeTooltip
                                title={
                                  <>
                                    <Typography fontSize='14px'>
                                      {topCode.description}
                                    </Typography>
                                  </>
                                }
                                followCursor
                              >
                                <Avatar
                                  sx={{
                                    bgcolor: topCodesColors[index],
                                    width: 100 + index * 25 + 'px',
                                    height: 100 + index * 25 + 'px',
                                    boxShadow: '0px 2px 4px #00000029',
                                  }}
                                >
                                  <Typography textAlign='center'>
                                    {topCode.code}
                                    <br />
                                    {topCode.claim_count}
                                  </Typography>
                                </Avatar>
                              </CodeTooltip>
                            </Box>
                          );
                        })}
                      </Grid>
                    </Box>
                  </>
                )}
              </Box>
            </Grid>
            <Grid item xs={6}>
              <Box className={classes.analyticsContainer}>
                {!loadedTopServiceLinesForSelectedProvider ? (
                  <LoadingView />
                ) : (
                  <>
                    <Typography
                      fontSize='18px'
                      color='#333333'
                      textAlign='center'
                    >
                      Top 3 Service Lines
                    </Typography>
                    <Box p={0}>
                      {categories.length > 0 && (
                        <Chart
                          type='bar'
                          height='300px'
                          options={{
                            chart: {
                              stacked: true,
                              toolbar: {
                                show: false,
                              },
                              animations: {
                                enabled: false,
                              },
                            },
                            xaxis: {
                              categories: categories,
                              labels: {
                                rotate: 0,
                              },
                            },
                            tooltip: {
                              enabled: true,
                              shared: true,
                              intersect: false,
                              x: {
                                formatter: (x) => {
                                  return `${x.join(' ')}`;
                                },
                              },
                              y: {
                                formatter: (val, opts) => {
                                  let showLessThan11 =
                                    series[opts.seriesIndex].data[
                                      opts.dataPointIndex
                                    ].lessThan11;
                                  if (showLessThan11) {
                                    return '< 11';
                                  }

                                  return StringUtil.numberForChart(val);
                                },
                              },
                            },
                            yaxis: {
                              show: false,
                              axisTicks: {
                                show: false,
                              },
                            },
                            dataLabels: {
                              enabled: false,
                            },
                            grid: {
                              show: false,
                            },
                            legend: {
                              show: false,
                            },
                            colors: ColorUtil.getColors(7),
                          }}
                          series={series}
                        />
                      )}
                    </Box>
                  </>
                )}
              </Box>
            </Grid>
          </Grid>
        </Box>
      </Grid>
    );
  };

  fetchData() {
    const { favoriteProviders } = this.props;

    if (!StoreUtil.isLoaded(favoriteProviders)) {
      this.props.dispatch(
        loadFavoriteProviders({
          clientId: this.props.clientId,
        })
      );
    }
  }

  fetchSelected() {
    const { dispatch, favoriteProviders } = this.props;
    let selectedFavoriteProvider =
      favoriteProviders.data[this.state.selectedProvider];
    if (selectedFavoriteProvider) {
      dispatch(
        loadProviderTopServiceLines({
          providerId: selectedFavoriteProvider.provider_id,
        })
      );

      dispatch(
        loadProviderTopDx({ providerId: selectedFavoriteProvider.provider_id })
      );
      dispatch(
        loadProviderTopPx({ providerId: selectedFavoriteProvider.provider_id })
      );
    }
  }

  selectProvider = (providerId, index) => {
    this.setState(
      {
        selectedProvider: index,
      },
      () => {
        this.fetchSelected();
      }
    );
  };

  clickTopDx = () => {
    this.setState({
      selectedTopCodesType: 'dx',
    });
  };

  clickTopPx = () => {
    this.setState({
      selectedTopCodesType: 'px',
    });
  };

  onSaveNewNote = (data) => {
    const { app, user, dispatch } = this.props;
    data.provider_id = this.state.newNoteForProviderId;
    if (
      data.body &&
      data.body.length > 0 &&
      (data.providers.length > 0 ||
        data.contacts.length > 0 ||
        data.location_id)
    ) {
      data.client_id = app.selectedClient;
      data.user_id = user.id;

      dispatch(upsertProviderComment(data));
    }
    this.closeNewNoteDialog();
  };

  closeNewNoteDialog = () => {
    this.setState({
      newNoteDialogOpen: false,
      newNoteForProviderId: -1,
    });
  };

  openNewNoteDialog = (providerId) => {
    if (this.props.user.is_admin) {
      this.props.dispatch(
        showConfirmationDialog({
          title: 'Admin Users Can Not Create Notes',
          content: 'Login as a non-admin user to create activity notes.',
          confirmLabel: 'OK',
          onConfirm: () => {
            // do nothing
          },
        })
      );
    } else {
      this.setState({
        newNoteDialogOpen: true,
        newNoteForProviderId: providerId,
      });
    }
  };
}

DashboardProvidersPage.propTypes = {
  isMobile: PropTypes.bool.isRequired,
  isEarlyMobile: PropTypes.bool.isRequired,
  classes: PropTypes.object.isRequired,
  app: PropTypes.object,
  user: PropTypes.object,
  router: PropTypes.object.isRequired,
  clientId: PropTypes.number.isRequired,
  dashboard: PropTypes.object,
  dispatch: PropTypes.func.isRequired,
  userId: PropTypes.number,
  favoriteProviders: PropTypes.object,
};

function mapStateToProps(state) {
  const { app, user, dashboard } = state;
  const userId = state.user ? state.user.id : null;
  const clientId = state.app.selectedClient;

  return {
    app,
    user,
    userId,
    clientId,
    dashboard,
    favoriteProviders: StoreUtil.get(
      dashboard,
      'favorite-providers-' + clientId
    ),
  };
}

const withStyled = withStyles(styles)(withIsMobile(DashboardProvidersPage));
const connected = connect(mapStateToProps)(withStyled);
const routed = withRouter(connected);
export { routed as DashboardProvidersPage };
